'use client';
import React from 'react';
import Image from 'next/image';
import { AnimatePresence, motion } from 'framer-motion';

import { useCart } from '@/context/cartContext';
import AnimatedGradientButton from '../shared/AnimatedGradientButton';

interface ReviewCartWidgetProps {
  isOpen: boolean;
  onClose: () => void;
}

const ReviewCartWidget: React.FC<ReviewCartWidgetProps> = ({
  isOpen,
  onClose,
}) => {
  // Type assertion for the entire useCart hook return value
  const { cart, removeFromCart, updateItemQuantity } = useCart();
  // Since we've typed the context properly, we can use cart directly
  const typedCart = cart;

  const totalPrice = typedCart.reduce((total, item) => {
    return total + parseFloat(item.price) * item.quantity;
  }, 0);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2, ease: 'easeInOut' }}
          className="fixed inset-0 z-50 z-1000 flex items-start justify-center bg-black/50 md:p-6"
        >
          <div className="flex w-full justify-end sm:max-w-[1200px]">
            <div className="w-full rounded-br-xl rounded-bl-xl bg-white p-6 shadow-lg sm:max-w-sm sm:rounded-lg">
              <div className="flex w-full items-center justify-between border-b border-[#C7C7C7] pb-3">
                <h2 className="font-['Work_Sans'] text-base leading-[25px] font-medium tracking-[2%] uppercase">
                  Review Your Cart
                </h2>
                <button
                  onClick={onClose}
                  className="text-gray-600 hover:text-black"
                >
                  <Image
                    src="/gradient-close-icon.svg"
                    height={45}
                    width={45}
                    alt="Close the review your cart widget"
                  />
                </button>
              </div>

              {typedCart.length === 0 ? (
                <div className="mt-8">
                  <span>Your cart is empty.</span>
                </div>
              ) : (
                <>
                  {typedCart.map((item, i) => (
                    <div
                      key={i}
                      className="flex justify-between gap-6 pt-5 font-['Work_Sans']"
                    >
                      {item.image.url && item.image.url.trim() !== '' ? (
                        <Image
                          src={item.image.url}
                          alt={item.title}
                          width={132}
                          height={132}
                          className="h-[132px] w-full max-w-[132px] rounded-[10px] object-contain object-center"
                        />
                      ) : (
                        <div className="flex h-[132px] w-full max-w-[132px] items-center justify-center rounded-[10px] bg-gray-200">
                          <span className="text-sm text-gray-500">
                            No image
                          </span>
                        </div>
                      )}

                      <div className="flex flex-col text-xl leading-7 font-normal tracking-[0%]">
                        <span>{item.title}</span>
                        <span>${parseFloat(item.price).toFixed(2)}</span>
                        <div className="mt-3.5 flex items-center">
                          <div className="flex items-center">
                            <button
                              className="flex h-[24px] items-center justify-center border border-black p-[2px_9px]"
                              onClick={() => {
                                if (item.quantity > 1) {
                                  updateItemQuantity(
                                    item.id,
                                    Math.max(1, item.quantity - 1)
                                  );
                                } else if (item.quantity === 1) {
                                  removeFromCart(item.id);
                                }
                              }}
                            >
                              -
                            </button>
                            <div className="flex h-[24px] items-center justify-center border-t border-b border-black p-[2px_20px] text-base">
                              {item.quantity}
                            </div>
                            <button
                              className="flex h-[24px] items-center justify-center border border-black p-[2px_9px]"
                              onClick={() =>
                                updateItemQuantity(item.id, item.quantity + 1)
                              }
                            >
                              +
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* Footer */}
              <div className="mt-8 flex justify-end">
                {typedCart.length > 0 ? (
                  <AnimatedGradientButton
                    href="/review-your-cart"
                    as="link"
                    theme="orange"
                    text={`Checkout $${totalPrice.toFixed(0)}`}
                    className="w-full min-w-[180px]"
                  />
                ) : (
                  <AnimatedGradientButton
                    as="button"
                    theme="orange"
                    text="Keep shopping"
                    onClick={onClose}
                    className="w-full min-w-[180px]"
                  />
                )}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ReviewCartWidget;
